/**
 * App-wide audio focus: at most ONE thing makes sound at a time.
 *
 * Two kinds of players share the speaker — the video scheduler
 * (`VideoAutoplayProvider`: feed/banner videos AND songs on static media, which
 * ride it as audio-only players) and on-demand song previews (`useMusicPreview`:
 * the profile CD case, pickers, chat song cards). Neither knows about the
 * other, so each claims focus here before making sound; claiming calls the
 * previous holder's `release`, which mutes or pauses it.
 *
 * Client-only module state. Releases are synchronous and must not throw.
 */

export interface AudioFocusHolder {
  /** Stable per player; re-claiming with the same id is a no-op. */
  id: string;
  /** Stop making sound now (pause or mute). */
  release: () => void;
}

let holder: AudioFocusHolder | null = null;

export function claimAudioFocus(next: AudioFocusHolder): void {
  if (holder && holder.id !== next.id) {
    const previous = holder;
    holder = next;
    try {
      previous.release();
    } catch {
      // A misbehaving player must not stop the new one from sounding.
    }
    return;
  }
  holder = next;
}

/** Give focus up without pausing anyone else (the holder went quiet). */
export function releaseAudioFocus(id: string): void {
  if (holder?.id === id) holder = null;
}

export function audioFocusHolderId(): string | null {
  return holder?.id ?? null;
}
